import { useEffect, useState } from "react";
import ToastMsg from "../../components/common/ToastMsg";
import { CUSTOM_CONFIGURATION } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import { Button } from "../../components/ui/button";
import { Checkbox } from "antd";
import { Save, Building, Calculator, FileText, Clock, Users, Cog } from "lucide-react";
import { Input } from "../../components/ui/input";

const SettingsConfiguration = () => {
    const { fetch, update } = useApiMethods();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [configData, setConfigData] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        pf_limit: 15000,
        cap_amount: 1800,
        zero_payroll: true,
        modify_issued_documents: false,
        biometric_punch_only_inout: false,
        grace_times: 0,
        deduct_for_contract_emp: true,
        is_deduction: true,
        salary_deduction: "gross",
        lop_deduction: "net_salary"
    });

    const fetchCustomConfig = async () => {
        setLoading(true);
        try {
            const response = await fetch(CUSTOM_CONFIGURATION);
            if (response.status === 'success' && response.data && response.data[0]) {
                const data = response.data[0];
                setConfigData(data);
                setFormData({
                    pf_limit: data.pf_limit ?? 15000,
                    cap_amount: data.cap_amount ?? 1800,
                    zero_payroll: data.zero_payroll !== undefined ? data.zero_payroll : true,
                    modify_issued_documents: data.modify_issued_documents || false,
                    biometric_punch_only_inout: data.biometric_punch_only_inout || false,
                    grace_times: data.grace_times || 0,
                    deduct_for_contract_emp: data.deduct_for_contract_emp !== undefined ? data.deduct_for_contract_emp : true,
                    is_deduction: data.is_deduction !== undefined ? data.is_deduction : true,
                    salary_deduction: data.salary_deduction || "gross",
                    lop_deduction: data.lop_deduction || "net_salary"
                });
            }
        } catch (error) {
            ToastMsg('error', error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                ...formData,
                id: configData?.id || 1
            };

            const response = await update(CUSTOM_CONFIGURATION, payload);

            if (response.status === 'success') {
                ToastMsg('success', 'Configuration saved successfully');
                fetchCustomConfig(); // Refresh data
            }
        } catch (error) {
            ToastMsg('error', error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    useEffect(() => {
        fetchCustomConfig();
    }, []);

    if (loading) {
        return (
            <div className="p-0">
                <div className="animate-pulse space-y-6">
                    {/* Header Skeleton */}
                    <div className="space-y-2">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/3"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/2"></div>
                    </div>

                    {/* Cards Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="bg-gray-200 dark:bg-gray-700 rounded-lg p-6 space-y-4">
                                {/* Card Header */}
                                <div className="flex items-center gap-3">
                                    <div className="h-5 w-5 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                                    <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded-lg w-32"></div>
                                </div>

                                {/* Card Content */}
                                <div className="space-y-3">
                                    {[...Array(3)].map((_, j) => (
                                        <div key={j} className="space-y-2">
                                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded-lg w-3/4"></div>
                                            <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded-lg w-1/2"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Save Button Skeleton */}
                    <div className="flex justify-end">
                        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-48"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-0">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <Cog className="h-8 w-8 text-sidebar-primary" />
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                        Custom Configuration
                    </h1>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                    Manage company-wide payroll and system configuration settings
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Provident Fund & Salary Settings */}
                <div className="space-y-6">
                    {/* Provident Fund Settings */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                        <div className="flex items-center gap-3 mb-6">
                            <Calculator className="h-5 w-5 text-blue-600" />
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                Provident Fund Settings
                            </h2>
                        </div>

                        <div className="space-y-4">
                            {/* Basic Salary Limit */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Basic Salary Limit
                                </label>
                                <Input
                                    type="number"
                                    value={formData.pf_limit}
                                    onChange={(e) => handleInputChange('pf_limit', parseInt(e.target.value) || 0)}
                                    prefix="₹"
                                    className="w-full"
                                    size="large"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Minimum basic salary for PF eligibility
                                </p>
                            </div>

                            {/* Cap Amount */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Cap Amount
                                </label>
                                <Input
                                    type="number"
                                    value={formData.cap_amount}
                                    onChange={(e) => handleInputChange('cap_amount', parseInt(e.target.value) || 0)}
                                    prefix="₹"
                                    className="w-full"
                                    size="large"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Maximum PF contribution amount
                                </p>
                            </div>

                            {/* Tax Deduction */}
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Tax Deduction (PF, ESIC, other..)
                                    </label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Enable tax deductions for employees
                                    </p>
                                </div>
                                <Checkbox
                                    checked={formData.is_deduction}
                                    onChange={(e) => handleInputChange('is_deduction', e.target.checked)}
                                    className="text-current"
                                >
                                    {formData.is_deduction ? 'Yes' : 'No'}
                                </Checkbox>
                            </div>
                        </div>
                    </div>

                    {/* LOP Deduction Settings */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                        <div className="flex items-center gap-3 mb-6">
                            <Calculator className="h-5 w-5 text-green-600" />
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                LOP Deduction
                            </h2>
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Gross Salary
                                    </label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Deduct LOP from gross salary
                                    </p>
                                </div>
                                <Checkbox
                                    checked={formData.lop_deduction === 'gross_salary'}
                                    onChange={(e) => handleInputChange('lop_deduction', e.target.checked ? 'gross_salary' : 'net_salary')}
                                    className="text-current"
                                >
                                    {formData.lop_deduction === 'gross_salary' ? 'Yes' : 'No'}
                                </Checkbox>
                            </div>

                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Net Salary
                                    </label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Deduct LOP from net salary
                                    </p>
                                </div>
                                <Checkbox
                                    checked={formData.lop_deduction === 'net_salary'}
                                    onChange={(e) => handleInputChange('lop_deduction', e.target.checked ? 'net_salary' : 'gross_salary')}
                                    className="text-current"
                                >
                                    {formData.lop_deduction === 'net_salary' ? 'Yes' : 'No'}
                                </Checkbox>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column - System Settings */}
                <div className="space-y-6">
                    {/* Payroll Settings */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                        <div className="flex items-center gap-3 mb-6">
                            <Calculator className="h-5 w-5 text-purple-600" />
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                Payroll Settings
                            </h2>
                        </div>

                        <div className="space-y-4">
                            {/* Zero Payroll */}
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Allow To Run Payroll For Zero Attendance
                                    </label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Enable payroll processing without attendance data
                                    </p>
                                </div>
                                <Checkbox
                                    checked={formData.zero_payroll}
                                    onChange={(e) => handleInputChange('zero_payroll', e.target.checked)}
                                    className="text-current"
                                >
                                    {formData.zero_payroll ? 'Yes' : 'No'}
                                </Checkbox>
                            </div>

                            {/* Contract Employee Deduction */}
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Deduct taxes for contract employee
                                    </label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Apply tax deductions to contract employees
                                    </p>
                                </div>
                                <Checkbox
                                    checked={formData.deduct_for_contract_emp}
                                    onChange={(e) => handleInputChange('deduct_for_contract_emp', e.target.checked)}
                                    className="text-current"
                                >
                                    {formData.deduct_for_contract_emp ? 'Yes' : 'No'}
                                </Checkbox>
                            </div>
                        </div>
                    </div>

                    {/* Document & System Settings */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                        <div className="flex items-center gap-3 mb-6">
                            <FileText className="h-5 w-5 text-orange-600" />
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                Document & System Settings
                            </h2>
                        </div>

                        <div className="space-y-4">
                            {/* Modify Issued Documents */}
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Allow User To Delete The Issued Documents
                                    </label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Permit document deletion after issuance
                                    </p>
                                </div>
                                <Checkbox
                                    checked={formData.modify_issued_documents}
                                    onChange={(e) => handleInputChange('modify_issued_documents', e.target.checked)}
                                    className="text-current"
                                >
                                    {formData.modify_issued_documents ? 'Yes' : 'No'}
                                </Checkbox>
                            </div>

                            {/* Biometric Punch */}
                            <div className="flex items-center justify-between py-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Biometric Punch Only IN/OUT
                                    </label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Restrict biometric to check-in/check-out only
                                    </p>
                                </div>
                                <Checkbox
                                    checked={formData.biometric_punch_only_inout}
                                    onChange={(e) => handleInputChange('biometric_punch_only_inout', e.target.checked)}
                                    className="text-current"
                                >
                                    {formData.biometric_punch_only_inout ? 'Yes' : 'No'}
                                </Checkbox>
                            </div>

                            {/* Grace Times */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Grace Times (Minutes)
                                </label>
                                <Input
                                    type="number"
                                    value={formData.grace_times}
                                    onChange={(e) => handleInputChange('grace_times', parseInt(e.target.value) || 0)}
                                    className="w-full"
                                    size="large"
                                    min="0"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Allowance time for late arrivals
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Save Button */}
            <div className="mt-8 flex justify-end">
                <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-sidebar hover:bg-sidebar-primary text-white font-medium py-2 px-6 rounded-md transition-colors duration-200 flex items-center gap-2"
                >
                    <Save className="h-4 w-4" />
                    {saving ? 'Saving...' : 'Save Configuration'}
                </Button>
            </div>
        </div>
    );
};

export default SettingsConfiguration;